import { Grid, IconButton, Typography, Box, Tooltip, Avatar } from '@mui/material';
import { useMemo } from 'react';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import { ActionButton } from '../../Button';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import DefaultConstants from '../../../data/Constants';
import { Edit } from '@mui/icons-material';
import useDialog from '../../../hooks/useDialog';
import FormHelper from '../../../helpers/FormHelper';
import { IDefaultStatusData, ServicePriorityData, ServicePriorityEnum, ServiceStatusData, ServiceStatusEnum } from '../../../data/Enums';
import IServiceOrder from '../../../interfaces/IServiceOrder';
import htmlParser from 'html-react-parser';
import ServiceOrderService from '../../../services/ServiceOrderService';
import UpdatePriorityDialog from './UpdatePriorityDialog';
import UpdateDepartmentDialog from './UpdateDepartmentDialog';
import UpdateResponsibleDialog from './UpdateResponsibleDialog';
import DateTimeHelper from '../../../helpers/DateTimeHelper';
import Chip from '../../Chip';
import UpdateStartDateDialog from './UpdateStartDateDialog';
import UpdateEstimatedCompletionDateDialog from './UpdateEstimatedCompletionDateDialog';


interface IServiceOrderInformationsDialogProps extends IDefaultDialogProps {
    serviceOrder: IServiceOrder | undefined;
    handleRefreshSelectedServiceOrder: () => void;
    editDepartment: boolean;
    editPriority: boolean;
    editResponsible: boolean;
    editStartDate: boolean;
    editEstimatedCompletionDate: boolean;
}


const ServiceOrderInformationsDialog: React.FC<IServiceOrderInformationsDialogProps> = ({
    open,
    handleClose,
    serviceOrder,
    handleRefreshSelectedServiceOrder,
    editDepartment,
    editPriority,
    editResponsible,
    editStartDate,
    editEstimatedCompletionDate
}) => {
    const { translate } = useLanguageContext();

    const updatePriorityDialog = useDialog();
    const updateDepartmentDialog = useDialog();
    const updateResponsibleDialog = useDialog();
    const updateStartDateDialog = useDialog();
    const updateEstimatedCompletionDateDialog = useDialog();


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const status = useMemo(() => {
        return Object.values(ServiceStatusData).find(s => s.key === +(serviceOrder?.statusId ?? ServiceStatusEnum.Pending)) as IDefaultStatusData;
    }, [serviceOrder]);

    const priority = useMemo(() => {
        return Object.values(ServicePriorityData).find(s => s.key === +(serviceOrder?.priorityId ?? ServicePriorityEnum.Empty)) as IDefaultStatusData;
    }, [serviceOrder]);
    

    return (
        <>
            <Dialog
                open={open}
                handleClose={handleClose}
                title={`# ${serviceOrder?.id}`}
                dialogActions={dialogActions}
                className='dialogactions-spacebetween overflow-inherit'
                absolute={true}
                dividers={true}
            >
                <Grid container rowSpacing={DefaultConstants.gridRowSpacing}>
                    <Grid item xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('createdBy')}
                        </Typography>

                        <Typography variant='body2' className='display-flex alignitems-center'>
                            <Avatar src={serviceOrder?.createdBy.fullName} alt={serviceOrder?.createdBy.fullName} className='inline-avatar' />
                            {serviceOrder?.createdBy.fullName}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('createdAt')}
                        </Typography>

                        <Typography variant='body2'>
                            {
                                translate('dateAtTime')
                                    .replace('{date}', DateTimeHelper.Format(serviceOrder?.createdAt, 'DD/MM/YYYY') ?? '')
                                    .replace('{time}', DateTimeHelper.Format(serviceOrder?.createdAt, 'HH:mm') ?? '')
                            }
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('room')}
                        </Typography>

                        <Typography variant='body2'>
                            {serviceOrder?.room?.description ?? '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('status')}
                        </Typography>

                        <Typography variant='body2' className='display-flex alignitems-center' sx={{ color: status.color }}>
                            <status.Icon className='inline-icon' />
                            {translate(status.name)}
                        </Typography>
                    </Grid>

                    <Grid item xs={6} className='display-flex'>
                        <Box paddingRight={2}>
                            <Typography variant='caption' className='fontsize-10 text-color-2'>
                                {translate('priority')}
                            </Typography>

                            <Typography variant='body2' className='display-flex alignitems-center' sx={{ color: priority.color }}>
                                <priority.Icon className='inline-icon' />
                                {translate(priority.name)}
                            </Typography>
                        </Box>

                        {
                            editPriority && <Box paddingTop={1}>
                                <Tooltip title={translate('edit')} placement="left" arrow disableInteractive>
                                    <IconButton onClick={() => updatePriorityDialog.handleOpen()}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    </Grid>

                    <Grid item xs={6} className='display-flex'>
                        <Box paddingRight={2}>
                            <Typography variant='caption' className='fontsize-10 text-color-2'>
                                {translate('department')}
                            </Typography>

                            <Typography variant='body2'>
                                {serviceOrder?.department?.name ?? '-'}
                            </Typography>
                        </Box>

                        {
                            editDepartment && <Box paddingTop={1}>
                                <Tooltip title={translate('edit')} placement="left" arrow disableInteractive>
                                    <IconButton onClick={() => updateDepartmentDialog.handleOpen()}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    </Grid>

                    <Grid item xs={12} className='display-flex'>
                        <Box paddingRight={2}>
                            <Typography variant='caption' className='fontsize-10 text-color-2'>
                                {translate('responsible')}
                            </Typography>

                            {
                                serviceOrder?.responsible ? (
                                    <Typography variant='body2' className='display-flex alignitems-center'>
                                        <Avatar src={serviceOrder.responsible.fullName} alt={serviceOrder.responsible.fullName} className='inline-avatar' />
                                        {serviceOrder.responsible.fullName}
                                    </Typography>
                                ) : (
                                    <Typography variant='body2'>-</Typography>
                                )
                            }
                        </Box>

                        {
                            editResponsible && <Box paddingTop={1}>
                                <Tooltip title={translate('edit')} placement="left" arrow disableInteractive>
                                    <IconButton onClick={() => updateResponsibleDialog.handleOpen()}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    </Grid>

                    <Grid item xs={6} className='display-flex'>
                        <Box paddingRight={2}>
                            <Typography variant='caption' className='fontsize-10 text-color-2'>
                                {translate('startDate')}
                            </Typography>

                            <Typography variant='body2'>
                                {serviceOrder?.startDate ? DateTimeHelper.Format(serviceOrder.startDate) : '-'}
                            </Typography>
                        </Box>

                        {
                            editStartDate && <Box paddingTop={1}>
                                <Tooltip title={translate('edit')} placement="left" arrow disableInteractive>
                                    <IconButton onClick={() => updateStartDateDialog.handleOpen()}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    </Grid>

                    <Grid item xs={6} className='display-flex'>
                        <Box paddingRight={2}>
                            <Typography variant='caption' className='fontsize-10 text-color-2'>
                                {translate('estimatedCompletionDate')}
                            </Typography>

                            <Typography variant='body2'>
                                {serviceOrder?.estimatedCompletionDate ? DateTimeHelper.Format(serviceOrder.estimatedCompletionDate) : '-'}
                            </Typography>
                        </Box>

                        {
                            editEstimatedCompletionDate && <Box paddingTop={1}>
                                <Tooltip title={translate('edit')} placement="left" arrow disableInteractive>
                                    <IconButton onClick={() => updateEstimatedCompletionDateDialog.handleOpen()}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('tags')}
                        </Typography>

                        {
                            serviceOrder !== undefined && serviceOrder.tags.length > 0 ? (
                                <Box className='display-flex flexwrap-wrap alignitems-center justifycontent-flexstart gap-6' paddingTop={1}>
                                    {
                                        serviceOrder.tags.map((t, index) => {
                                            return <Chip label={t} size='small' key={index} />
                                        })
                                    }
                                </Box>
                            ) : (
                                <Typography variant='body2'>-</Typography>
                            )
                        }
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('observations')}
                        </Typography>

                        <Typography variant='body2' className='htmlparser-marginnone'>
                            {
                                serviceOrder !== undefined && serviceOrder.description.trim().length > 8 ? (
                                    htmlParser(serviceOrder.description)
                                ) : (
                                    translate('noDescriptionFound')
                                )
                            }
                        </Typography>
                    </Grid>
                </Grid>
            </Dialog>

            {
                serviceOrder !== undefined && editPriority && <UpdatePriorityDialog
                    open={updatePriorityDialog.open}
                    handleClose={() => updatePriorityDialog.handleClose()}
                    serviceOrder={serviceOrder}
                    handleSubmit={(event) => {
                        event.preventDefault();
                
                        let formData = FormHelper.Create(event);
                        formData = FormHelper.Add(formData, 'id', serviceOrder.id);
                        
                        ServiceOrderService.UpdatePriority(formData).then(() => {
                            handleRefreshSelectedServiceOrder();
                            updatePriorityDialog.handleClose();
                        });
                    }}
                />
            }

            {
                serviceOrder !== undefined && editDepartment && <UpdateDepartmentDialog
                    open={updateDepartmentDialog.open}
                    handleClose={() => updateDepartmentDialog.handleClose()}
                    serviceOrder={serviceOrder}
                    handleSubmit={(event) => {
                        event.preventDefault();
                
                        let formData = FormHelper.Create(event);
                        formData = FormHelper.Add(formData, 'id', serviceOrder.id);
                        
                        ServiceOrderService.UpdateDepartment(formData).then(() => {
                            handleRefreshSelectedServiceOrder();
                            updateDepartmentDialog.handleClose();
                        });
                    }}
                />
            }

            {
                serviceOrder !== undefined && editResponsible && <UpdateResponsibleDialog
                    open={updateResponsibleDialog.open}
                    handleClose={() => updateResponsibleDialog.handleClose()}
                    serviceOrder={serviceOrder}
                    handleSubmit={(event) => {
                        event.preventDefault();
                
                        let formData = FormHelper.Create(event);
                        formData = FormHelper.Add(formData, 'id', serviceOrder.id);
                        
                        ServiceOrderService.UpdateResponsible(formData).then(() => {
                            handleRefreshSelectedServiceOrder();
                            updateResponsibleDialog.handleClose();
                        });
                    }}
                />
            }

            {
                serviceOrder !== undefined && editStartDate && <UpdateStartDateDialog
                    open={updateStartDateDialog.open}
                    handleClose={() => updateStartDateDialog.handleClose()}
                    serviceOrder={serviceOrder}
                    handleSubmit={(event) => {
                        event.preventDefault();
                
                        let formData = FormHelper.Create(event);
                        formData = FormHelper.Add(formData, 'id', serviceOrder.id);
                        
                        ServiceOrderService.UpdateStartDate(formData).then(() => {
                            handleRefreshSelectedServiceOrder();
                            updateStartDateDialog.handleClose();
                        });
                    }}
                />
            }

            {
                serviceOrder !== undefined && editEstimatedCompletionDate && <UpdateEstimatedCompletionDateDialog
                    open={updateEstimatedCompletionDateDialog.open}
                    handleClose={() => updateEstimatedCompletionDateDialog.handleClose()}
                    serviceOrder={serviceOrder}
                    handleSubmit={(event) => {
                        event.preventDefault();
                
                        let formData = FormHelper.Create(event);
                        formData = FormHelper.Add(formData, 'id', serviceOrder.id);
                        
                        ServiceOrderService.UpdateEstimatedCompletionDate(formData).then(() => {
                            handleRefreshSelectedServiceOrder();
                            updateEstimatedCompletionDateDialog.handleClose();
                        });
                    }}
                />
            }
        </>
    );
}

export default ServiceOrderInformationsDialog;